import { useEffect, useState } from 'react'
import { getApiErrorMessage } from '../lib/utils'
import useApi from './useApi'
import useCleanup from './useCleanup'

type Company = {
	id: string
	name: string
	address: string
	created_at: string
	industry_codes: string[]
	currency_code?: string
	jurisdiction_code: string
	number: number
	number_of_employees?: number
	turnover?: number
	type?: string
	updated_at: string
}

type CompanyDataState = {
	companyId?: string
	api: ReturnType<typeof useApi>
	companyData: Company | null
	isLoading: boolean
	error: string | null
}

const useCompanyData = (companyId?: string) => {
	const api = useApi()
	const [state, setState] = useState<CompanyDataState>({
		companyId, api, companyData: null, isLoading: true, error: null,
	})

	const refetchCompanyData = useCleanup(() => {
		let cancelled = false
		setState({ companyId, api, companyData: null, isLoading: true, error: null })
		const run = async () => {
			try {
				if (!companyId) throw new Error('No organisation is selected')
				const body = await api.get(`company/${companyId}`)
				const storedCodes = body.company.industryCodes.map((ic: { code: string }) => ic.code)
				if (cancelled) return
				setState({
					companyId, api, companyData: { ...body.company, industry_codes: storedCodes },
					isLoading: false, error: null,
				})
			} catch (error) {
				if (cancelled) return
				setState({
					companyId, api, companyData: null, isLoading: false,
					error: getApiErrorMessage(error, 'Could not load organisation data. Please try again.'),
				})
			}
		}
		run()
		return () => { cancelled = true }
	})

	useEffect(() => {
		refetchCompanyData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, api])

	const isCurrent = state.companyId === companyId && state.api === api
	return {
		companyData: isCurrent ? state.companyData : null,
		isLoading: !isCurrent || state.isLoading,
		error: isCurrent ? state.error : null,
		refetchCompanyData,
	}
}

export default useCompanyData
